import { num } from '../../lib/format'
import {
  GpxError,
  MAX_GPX_BYTES,
  nachFahrtzeit,
  trackKonflikt,
  type TrackKonflikt,
} from '../../lib/gpx'
import type { TrackPart } from '../../lib/types'

/**
 * Mehrere GPX-Dateien auf einmal einlesen und beurteilen.
 *
 * Der Anlass ist gemessen und nicht ausgedacht: ein gewöhnlicher Arbeitstag
 * ergibt vier Dateien, weil das Gerät nach fünf Minuten Stillstand in
 * Bereitschaft geht und je Fahrt eine schreibt. Wer sie einzeln hochladen muss,
 * lädt viermal hoch und sieht viermal eine Liste, in der jedes Mal eine andere
 * Zahl steht.
 *
 * Reine Funktionen, kein React: was aus fünf Dateien eine Zahl macht, gehört
 * unter Test. Die Bedienung dazu steht in TrackPanel.tsx (eine Etappe), in
 * TrackVerteilen.tsx (mehrere Tage) und in EtappenAusAufzeichnung.tsx (neue
 * Etappen); die Regeln, nach denen Teile zusammengehen, stehen in lib/gpx.ts.
 *
 * Geteilt wird zwischen den drei Masken, was in allen dreien dasselbe ist: das
 * Lesen selbst (leseDateien) und die Warnung neben einer halb gelesenen Datei
 * (warnungenAus). Was danach mit den Stücken geschieht, ist in jeder Maske
 * etwas anderes und steht deshalb nicht hier - die dritte legt Etappen an und
 * sortiert nach Kalendertagen (anlegen.ts), die beiden anderen legen zu einer
 * bestehenden Etappe dazu und arbeiten mit Kandidaten (weiter unten).
 *
 * ## Drei Fragen, die erst bei mehreren Dateien entstehen
 *
 * Die Reihenfolge steht in nachFahrtzeit (lib/gpx.ts): der erste Zeitstempel
 * entscheidet, nicht der Dateiname.
 *
 * Was eine kaputte Datei kostet, steht in leseDateien: sich selbst und sonst
 * nichts.
 *
 * Was eine doppelte Datei kostet, steht in ersteKandidaten: nichts, denn sie
 * wird erkannt, bevor sie in die Kilometer geht.
 */

/**
 * Was von einer Datei gebraucht wird, um sie zu lesen.
 *
 * Ein File aus einem Dateidialog erfüllt das. Als eigene Schnittstelle
 * geschrieben und nicht als File, damit diese Datei ohne DOM prüfbar bleibt -
 * die Tests laufen in der Umgebung node, und ein zweiter Umgebungssatz nur für
 * diese Funktion wäre der falsche Preis für eine Schleife über drei Felder.
 */
export interface Lesbar {
  name: string
  /** Grösse in Bytes. */
  size: number
  text(): Promise<string>
}

/** Eine Datei, die gar nicht erst zu einer Aufzeichnung wurde. */
export interface Lesefehler {
  name: string
  /** Was ihr fehlt, als Satz für die Anzeige. */
  grund: string
}

/** Was aus einer Datei geworden ist, mit ihrem Namen daneben. */
export interface Dateilese<T> {
  name: string
  wert: T
}

/** Das Ergebnis eines Durchgangs über mehrere Dateien. */
export interface Mehrlese<T> {
  /** Die Dateien, die etwas ergeben haben, in der Reihenfolge der Auswahl. */
  gelesen: Dateilese<T>[]
  /** Die Dateien, die nichts ergeben haben. */
  fehler: Lesefehler[]
}

/**
 * Liest mehrere Dateien, eine nach der anderen.
 *
 * ## Warum eine kaputte Datei nur sich selbst kostet
 *
 * Vier gute und eine unbrauchbare dürfen nicht alle fünf kosten. Wer fünf
 * Dateien wählt und eine Fehlermeldung bekommt, weiss nicht einmal, welche der
 * fünf gemeint ist - und muss die vier anderen von Hand noch einmal suchen.
 *
 * Drei Arten von Schaden gibt es, und sie werden verschieden behandelt. Zwei
 * davon kennt der Leser in lib/gpx.ts schon: ein einzelner unbrauchbarer Punkt
 * kostet diesen Punkt (dropped), abgeschnittenes Markup kostet den Rest der
 * Datei und wird als Warnung gemeldet (truncated) - beides ergibt trotzdem eine
 * Aufzeichnung, und beides steht in dem, was hier zurückkommt. Die dritte ist
 * die Datei, aus der überhaupt nichts wird: kein GPX, kein Trackpunkt, ein
 * einziger Punkt, zu gross. Sie steht in fehler, mit ihrem Namen, und die
 * anderen laufen weiter.
 *
 * ## Warum die Schranke je Datei gilt und nicht für die Summe
 *
 * MAX_GPX_BYTES begrenzt eine Datei (siehe dort: eine Zeichenkette kostet im
 * Browser das Doppelte ihrer Bytes). Fünf Dateien knapp darunter sind zusammen
 * ein Vielfaches davon - aber nie gleichzeitig: gelesen wird eine nach der
 * anderen, und was aus einer Datei bleibt, ist der fertige, ausgedünnte Track
 * mit ein paar hundert Stützpunkten. Die Zeichenkette und die Rohpunkte sind
 * danach unerreichbar und werden aufgeräumt. Der höchste Stand ist damit eine
 * Datei und nicht ihre Summe, und genau das ist die Zahl, die MAX_GPX_BYTES
 * meint.
 *
 * Der Reihe nach und nicht alle auf einmal ist deshalb keine Bequemlichkeit,
 * sondern die Bedingung dafür, dass die Schranke weiter das tut, was sie
 * verspricht.
 *
 * @param lies Was aus dem Inhalt einer Datei werden soll. Wirft die Funktion
 *   einen GpxError, gilt die Datei als unbrauchbar - und nur sie.
 */
export async function leseDateien<T>(
  dateien: Iterable<Lesbar>,
  lies: (xml: string, name: string) => T,
): Promise<Mehrlese<T>> {
  const gelesen: Dateilese<T>[] = []
  const fehler: Lesefehler[] = []

  for (const datei of dateien) {
    try {
      // Vor dem Lesen und nicht danach: text() zieht die ganze Datei in den
      // Speicher, und eine falsch gewählte Datei - ein Video aus demselben
      // Ordner - hätte das Telefon damit schon erledigt, bevor überhaupt
      // jemand nachgesehen hat, ob GPX darinsteht.
      if (datei.size > MAX_GPX_BYTES) {
        throw new GpxError(
          `Sie ist mit ${num(Math.ceil(datei.size / 1024 / 1024))} MB zu gross. ` +
            `Gelesen werden höchstens ${num(MAX_GPX_BYTES / 1024 / 1024)} MB je Datei.`,
        )
      }
      gelesen.push({ name: datei.name, wert: lies(await datei.text(), datei.name) })
    } catch (err) {
      if (err instanceof GpxError) {
        fehler.push({ name: datei.name, grund: err.message })
      } else {
        console.error('GPX-Datei konnte nicht gelesen werden', datei.name, err)
        fehler.push({ name: datei.name, grund: 'Sie liess sich nicht lesen.' })
      }
    }
  }

  return { gelesen, fehler }
}

/**
 * Die Sätze zu den Dateien, die zwar etwas ergeben haben, aber nicht ganz.
 *
 * Die mittlere der drei Arten von Schaden: abgeschnittenes Markup kostet den
 * Rest der Datei, nicht die Datei. Ihr Track steht in der Liste und zählt in
 * den Kilometern - daneben steht, ab wo nichts mehr kommt. Ein solcher Satz
 * gehört ausdrücklich nicht zu den Lesefehlern: wer ihn dort fände, suchte
 * eine Aufzeichnung, die längst dasteht.
 *
 * Der Dateiname steht davor, denn bei mehreren gewählten Dateien ist der Satz
 * ohne ihn nicht zuzuordnen.
 */
export function warnungenAus<T extends { warning: string | null }>(
  gelesen: Dateilese<T>[],
): string[] {
  return gelesen.flatMap((g) => (g.wert.warning === null ? [] : [`${g.name}: ${g.wert.warning}`]))
}

/** Eine gelesene Aufzeichnung, die noch nicht geschrieben ist. */
export interface Kandidat {
  teil: TrackPart
  /** Was ihrer Übernahme im Weg steht, oder null. */
  konflikt: TrackKonflikt | null
  /**
   * Ob das Gegenstück des Konflikts auf der Etappe liegt. Sonst steht es in
   * derselben Auswahl.
   *
   * Hier vermerkt und nicht in der Anzeige nachgeschlagen: dort liesse es sich
   * nur über die Gleichheit der Verweise feststellen, und die hält nicht - ein
   * Track aus einer einzigen Datei bekommt seine Teileliste bei jedem
   * Zeichnen neu gebaut (trackParts). Der Unterschied gehört in den Satz, der
   * dem Nutzer gezeigt wird: er sagt ihm, wo er nachsehen muss.
   */
  imBestand: boolean
  /** Ob sie beim Übernehmen mitgeht. */
  aktiv: boolean
}

/**
 * Beurteilt die Kandidaten der Reihe nach gegen alles, was vor ihnen liegt.
 *
 * Der Reihe nach und nicht jeden für sich: die zweite gewählte Datei muss auch
 * gegen die erste geprüft werden und nicht nur gegen das, was auf der Etappe
 * schon liegt. Genau dort steckt der Fall, um den es geht - beim Garmin enthält
 * Current.gpx dieselbe Spur noch einmal, und wer sie neben der Einzeldatei
 * mitwählt, hätte die Fahrt zweimal in den Kilometern.
 *
 * Ein Kandidat, der nicht mitgeht, blockiert nichts: er wandert nicht in die
 * Liste, gegen die der nächste geprüft wird. Fünf Dateien, von denen die zweite
 * eine Wiederholung der ersten ist, ergeben also vier Aufzeichnungen und nicht
 * drei.
 *
 * Umgekehrt zählt ein angehakter Kandidat mit, auch wenn er selbst strittig
 * ist: wer eine Wiederholung ausdrücklich haben will, bekommt sie - und der
 * nächste Kandidat wird gegen das gemessen, was dann wirklich dasteht.
 */
function beurteile(
  vorhanden: TrackPart[],
  kandidaten: Kandidat[],
  neuEntscheiden: boolean,
): Kandidat[] {
  const angenommen = [...vorhanden]

  return kandidaten.map((kandidat) => {
    const konflikt = trackKonflikt(angenommen, kandidat.teil)
    const aktiv = neuEntscheiden ? konflikt === null : kandidat.aktiv
    if (aktiv) angenommen.push(kandidat.teil)
    return {
      ...kandidat,
      konflikt,
      // Die Gleichheit der Verweise trägt genau hier, und nur hier: die Liste,
      // in der gesucht wird, ist dieselbe, aus der trackKonflikt sein
      // Gegenstück genommen hat.
      imBestand: konflikt !== null && vorhanden.includes(konflikt.teil),
      aktiv,
    }
  })
}

/**
 * Macht aus frisch gelesenen Aufzeichnungen die Liste, die dem Nutzer vorgelegt
 * wird.
 *
 * Sortiert nach der Uhr (nachFahrtzeit), beurteilt gegen das, was auf der
 * Etappe schon liegt, und vorgehakt ist nur, was unstrittig ist. Alles andere
 * steht da, mit Grund, und wartet auf eine Entscheidung - übernommen wird
 * nichts, bevor der Nutzer die Liste gesehen hat.
 *
 * @param vorhanden Die Teile, die auf der Etappe schon liegen. Leer beim ersten
 *   Import.
 */
export function ersteKandidaten(vorhanden: TrackPart[], neue: TrackPart[]): Kandidat[] {
  return beurteile(
    vorhanden,
    nachFahrtzeit(neue).map((teil) => ({ teil, konflikt: null, imBestand: false, aktiv: true })),
    true,
  )
}

/**
 * Rechnet die Beurteilungen neu, nachdem der Nutzer einen Haken bewegt hat.
 *
 * Nötig, weil die Beurteilung eines Kandidaten davon abhängt, was vor ihm
 * angenommen wird: wer die doppelte Datei abwählt, macht damit die Datei
 * dahinter unstrittig. Die Haken bleiben dabei, wie der Nutzer sie gesetzt hat -
 * hier wird gerechnet und nicht entschieden.
 */
export function neuBeurteilt(vorhanden: TrackPart[], kandidaten: Kandidat[]): Kandidat[] {
  return beurteile(vorhanden, kandidaten, false)
}

/** Die Teile, die beim Übernehmen mitgehen. */
export function angenommene(kandidaten: Kandidat[]): TrackPart[] {
  return kandidaten.filter((k) => k.aktiv).map((k) => k.teil)
}
